import React, { useContext, useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Users,
  Mail,
  Boxes,
  CreditCard,
  ScrollText,
  Settings,
  PlayCircle,
  LogOut,
} from "lucide-react";
import { AuthContext } from "../context/AuthContext";

const NAV_ITEMS = [
  { to: "/superadmin",                 end: true,  label: "Dashboard",     icon: LayoutDashboard },
  { to: "/superadmin/orgs",            end: false, label: "Organisations", icon: Building2 },
  { to: "/superadmin/users",           end: false, label: "Users",         icon: Users },
  { to: "/superadmin/staff-invites",   end: false, label: "Invites",       icon: Mail },
  { to: "/superadmin/modules",         end: false, label: "Modules",       icon: Boxes },
  { to: "/superadmin/billing",         end: false, label: "Billing",       icon: CreditCard },
  { to: "/superadmin/demos",           end: false, label: "Demos",         icon: PlayCircle },
  { to: "/superadmin/audit",           end: false, label: "Audit Log",     icon: ScrollText },
  { to: "/superadmin/settings",        end: false, label: "Settings",      icon: Settings },
];

const PAGE_TITLES = {
  "/superadmin":               "Dashboard",
  "/superadmin/orgs":          "Organisations",
  "/superadmin/users":         "Users",
  "/superadmin/staff-invites": "Staff Invites",
  "/superadmin/modules":       "Modules",
  "/superadmin/billing":       "Billing",
  "/superadmin/demos":         "Demo Sessions",
  "/superadmin/audit":         "Audit Log",
  "/superadmin/settings":      "Settings",
};

function SidebarNav() {
  const linkClass = ({ isActive }) =>
    "flex items-center gap-3 pl-4 pr-3 py-2.5 text-sm transition border-l-2 " +
    (isActive
      ? "bg-[#00FF99]/10 text-[#00FF99] border-[#00FF99]"
      : "text-white/70 hover:text-white hover:bg-white/[0.04] border-transparent");

  return (
    <nav className="flex-1 overflow-y-auto py-2">
      {NAV_ITEMS.map(({ to, end, label, icon: Icon }) => (
        <NavLink key={to} to={to} end={end} className={linkClass}>
          <Icon size={16} className="shrink-0" />
          <span className="truncate">{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarFooter() {
  const { user, logout } = useContext(AuthContext);
  const handleLogout = async (e) => {
    e.preventDefault();
    try { await logout(); } catch {}
    window.location.href = "/login";
  };

  return (
    <div className="border-t border-white/10 p-3 space-y-2">
      <div className="px-1 text-xs text-white/40 truncate" title={user?.email || ""}>
        {user?.email || "—"}
      </div>
      <button
        onClick={handleLogout}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md text-white/70 hover:text-red-400 hover:bg-red-500/10 transition"
      >
        <LogOut size={16} />
        Logout
      </button>
    </div>
  );
}

function TopBar({ title }) {
  const { user } = useContext(AuthContext);
  return (
    <div className="h-[60px] flex items-center justify-between px-6 border-b border-white/10 bg-black/70 backdrop-blur-md sticky top-0 z-30">
      <h1 className="text-lg font-semibold text-white tracking-tight">
        {title}
      </h1>
      <div className="flex items-center gap-3">
        <span className="text-sm text-white/70 hidden sm:inline">
          {user?.full_name || user?.email || "—"}
        </span>
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#00FF99]/10 text-[#00FF99] border border-[#00FF99]/30">
          Superadmin
        </span>
      </div>
    </div>
  );
}

export default function SuperadminLayout({ children }) {
  const { pathname } = useLocation();

  const title = useMemo(() => {
    if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
    // fallback: longest matching prefix
    const match = Object.keys(PAGE_TITLES)
      .filter((k) => k !== "/superadmin" && pathname.startsWith(k))
      .sort((a, b) => b.length - a.length)[0];
    return PAGE_TITLES[match] || "Superadmin";
  }, [pathname]);

  return (
    <div className="h-screen w-screen flex bg-black text-white overflow-hidden">
      {/* ---------------- LEFT SIDEBAR ---------------- */}
      <aside className="w-[240px] shrink-0 h-full flex flex-col border-r border-white/10 bg-[#0a0a0a]">
        <div className="px-5 pt-5 pb-3">
          <div className="text-2xl font-bold text-[#00FF99] tracking-tight drop-shadow-[0_0_8px_#00FF9955]">
            KweryAI
          </div>
          <div className="text-[11px] text-white/40 uppercase tracking-wider mt-0.5">
            Superadmin Console
          </div>
        </div>
        <div className="border-t border-white/10" />
        <SidebarNav />
        <SidebarFooter />
      </aside>

      {/* ---------------- MAIN ---------------- */}
      <main className="flex-1 flex flex-col h-full overflow-hidden">
        <TopBar title={title} />
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">{children}</div>
        </div>
      </main>
    </div>
  );
}
